import React, { createContext, useContext } from 'react';

/**
 * Navigation Context
 * Eliminates prop drilling for navigation callbacks across 10+ components
 * All navigation functions are provided by App.js and consumed by child components
 */
const NavigationContext = createContext(null);

/**
 * Navigation Provider - wrap your app with this component
 * @param {Object} props.navigation - Object containing all navigation functions
 */
export const NavigationProvider = ({ children, navigation }) => {
  return (
    <NavigationContext.Provider value={navigation}>
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Custom hook to access navigation functions
 * Usage: const { onNavigateToProfile, onNavigateToLandingPage } = useNavigation();
 *
 * Available navigation functions (all use 'onNavigateTo' prefix):
 * - onNavigateToLogin
 * - onNavigateToRegister
 * - onNavigateToForgotPassword
 * - onNavigateToLandingPage
 * - onNavigateToProfile
 * - onNavigateToChangePassword
 * - onNavigateToLanguageSettings
 * - onNavigateToDisplaySettings (Issue #98: Dark Mode)
 * - onNavigateToCourseReview
 * - onNavigateToCourseDetail(course)
 * - onNavigateToMyPosts
 * - onNavigateToMeinMarktplatz
 * - onNavigateToMarketplaceDetail(object)
 * - onNavigateToSellerContact(object)
 * - onNavigateToChat(conversationId)
 * - onNavigateToChatList
 * - onNavigateToNotifications (Issue #87)
 * - onNavigateToPost(postId) (Issue #87)
 * - onNavigateToLandingWithCreatePost
 * - onNavigateToLandingWithCreateMarketplace
 * - onNavigateBack
 * - onLogout
 */
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export default NavigationContext;
